<template>
  <div class="container mx-auto p-4">
    <!-- New Integrations List -->
    <table class="table-auto w-full mb-4">
      <thead>
        <tr class="bg-gray-200">
          <th class="px-4 py-2">Name</th>
          <th class="px-4 py-2">Start Date</th>
          <th class="px-4 py-2">End Date</th>
          <th class="px-4 py-2">Integration Codes CSV</th>
          <th class="px-4 py-2">Disallowed Event Names</th>
          <th class="px-4 py-2">Distinct Events</th>
          <th class="px-4 py-2">Linked Native Display Campaign</th>
          <th class="px-4 py-2">Updated At</th>
          <th class="px-4 py-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="integration in integrations"
          :key="integration.uuid"
          :class="{ 'bg-blue-100': integration.uuid === activeRowId }"
          class="border-b"
        >
          <td class="px-4 py-2 break-words whitespace-normal max-w-xs">
            {{ integration.name }}
          </td>
          <td class="px-4 py-2">
            {{ formatToMMddYYYY(integration.start_date) }}
          </td>
          <td class="px-4 py-2">
            {{ formatToMMddYYYY(integration.end_date) }}
          </td>
          <td class="px-4 py-2 break-words whitespace-normal max-w-xs">
            {{ integration.integration_codes_csv }}
          </td>
          <!-- Disallowed Event Names -->
          <td class="px-4 py-2 whitespace-normal relative max-w-lg">
            <div v-for="(name, idx) in (integration.disallowed_event_names_csv ?? '').split(',').map(n => n.trim()).slice(0, 4)"
                 :key="'dis-'+idx"
                 class="text-sm">
              {{ name }}
            </div>
            <button
              v-if="(integration.disallowed_event_names_csv ?? '').split(',').length > 4"
              @click="toggleDisallowed(integration.uuid)"
              class="mt-1 text-blue-500 underline text-sm"
            >
              {{ showDisallowedId === integration.uuid ? 'Hide' : 'Show All' }}
            </button>
            <div
              v-if="showDisallowedId === integration.uuid"
              class="absolute top-full left-0 bg-white border rounded shadow-lg p-2 mt-1 z-10 min-w-max max-w-[calc(100vw-2rem)] max-h-64 overflow-y-auto"
            >
              <div
                v-for="(name, idx) in (integration.disallowed_event_names_csv ?? '').split(',').map(n => n.trim())"
                :key="'disfull-'+idx"
                class="text-sm py-1"
              >
                {{ name }}
              </div>
            </div>
          </td>
          <!-- Distinct Events -->
          <td class="px-4 py-2 whitespace-normal relative max-w-lg">
            <template v-if="distinctEvents[integration.uuid]">
              <div v-for="(name, idx) in distinctEvents[integration.uuid].slice(0, 4)"
                   :key="'evt-'+idx"
                   class="text-sm"
              >
                {{ name }}
              </div>
              <button
                v-if="distinctEvents[integration.uuid].length > 4"
                @click="toggleDistinct(integration.uuid)"
                class="mt-1 text-blue-500 underline text-sm"
              >
                {{ showDistinctId === integration.uuid ? 'Hide' : 'Show All' }}
              </button>
              <div
                v-if="showDistinctId === integration.uuid"
                class="absolute top-full left-0 bg-white border rounded shadow-lg p-2 mt-1 z-10 min-w-max max-w-[calc(100vw-2rem)] max-h-64 overflow-y-auto"
              >
                <div
                  v-for="(name, idx) in distinctEvents[integration.uuid]"
                  :key="'evtfull-'+idx"
                  class="text-sm py-1"
                >
                  {{ name }}
                </div>
              </div>
            </template>
            <button
              v-else
              @click="loadDistinct(integration)"
              class="text-blue-500 underline text-sm"
            >
              Load Events
            </button>
          </td>
          <td class="px-4 py-2">
            {{ integrationCampaignName(integration) }}
          </td>
          <td class="px-4 py-2">
            {{ formatToMMddYYYY(integration.updated_at) }}
          </td>
          <td class="px-4 py-2 flex flex-col items-start">
            <button
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded mb-2"
              @click="editIntegration(integration)"
            >
              Edit
            </button>
            <button
              class="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
              @click="deleteIntegration(integration.uuid)"
            >
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Add/Edit Integration Form -->
    <div v-if="showForm" class="mb-4">
      <form
        @submit.prevent="submitForm"
        class="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4"
      >
        <!-- Name -->
        <div class="mb-4">
          <label
            class="block text-gray-700 text-sm font-bold mb-2"
            for="name"
          >
            Name
          </label>
          <input
            id="name"
            type="text"
            v-model="form.name"
            placeholder="Integration Name"
            class="shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
          >
        </div>

        <!-- Start Date -->
        <div class="mb-4">
          <label
            class="block text-gray-700 text-sm font-bold mb-2"
            for="start-date"
          >
            Start Date
          </label>
          <input
            id="start-date"
            type="date"
            v-model="form.start_date"
            class="shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
          >
        </div>

        <!-- End Date -->
        <div class="mb-4">
          <label
            class="block text-gray-700 text-sm font-bold mb-2"
            for="end-date"
          >
            End Date
          </label>
          <input
            id="end-date"
            type="date"
            v-model="form.end_date"
            class="shadow appearance-none border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
          >
        </div>

        <!-- Integration Codes CSV -->
        <div class="mb-4">
          <label
            class="block text-gray-700 text-sm font-bold mb-2"
            for="integration-code"
          >
            Integration Codes CSV
          </label>
          <textarea
            id="integration-code"
            v-model="form.integration_codes_csv"
            placeholder="Enter Integration Code"
            class="shadow border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline resize-y overflow-auto h-32"
          ></textarea>
        </div>

        <!-- Disallowed Event Names CSV -->
        <div class="mb-4">
          <label
            class="block text-gray-700 text-sm font-bold mb-2"
            for="disallowed-events"
          >
            Disallowed Event Names CSV
          </label>
          <textarea
            id="disallowed-events"
            v-model="form.disallowed_event_names_csv"
            placeholder="Enter Disallowed Event Names"
            class="shadow border rounded w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline resize-y overflow-auto h-32"
          ></textarea>
        </div>

        <!-- Linked Campaign Dropdown -->
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2">
            Linked Native Display Campaign
          </label>
          <DropdownComponent
            v-if="allCampaigns.length"
            :items="allCampaigns"
            display="name"
            :selected="currentCampaign ? currentCampaign.name : 'Select a Campaign'"
            @user_select="setCurrentCampaign"
            class="sm:w-96 w-72 self-center"
          />
        </div>

        <div class="flex items-center justify-between">
          <button
            type="submit"
            class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Save
          </button>
        </div>
      </form>
    </div>

    <button
      class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
      @click="addIntegration"
    >
      Add Integration
    </button>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: "ManageNewIntegrations",
  data() {
    return {
      integrations: [],
      showForm: false,
      isEditMode: false,
      activeRowId: null,
      showDisallowedId: null,
      distinctEvents: {},
      showDistinctId: null,
      form: {
        name: '',
        start_date: '',
        end_date: '',
        integration_codes_csv: '',
        disallowed_event_names_csv: '',    // added
        native_display_campaign_uuid: null,
      },
      allCampaigns: [],
      currentIntegrationId: null,
    }
  },
  computed: {
    currentCampaign() {
      return this.allCampaigns.find(c => c.uuid === this.form.native_display_campaign_uuid);
    }
  },
  created() {
    this.getAllCampaigns();
    this.fetchIntegrations();
  },
  methods: {
    toggleDistinct(id) {
      this.showDistinctId = this.showDistinctId === id ? null : id;
    },
    async loadDistinct(integration) {
      const { data } = await this.$http.get(
        `/new-integrations/${integration.uuid}/distinct-events`
      );
      // Vue 3: assign directly to reactive object
      this.distinctEvents[integration.uuid] = data;
    },
    toggleDisallowed(id) {
      this.showDisallowedId = this.showDisallowedId === id ? null : id;
    },
    async fetchIntegrations() {
      const response = await this.$http_request(this.$http.get, '/new-integrations', {
        success: 'New Integrations fetched successfully',
        error:   'Failed to fetch new integrations'
      });
      this.integrations = response.data.data;
      this.distinctEvents = {};
      this.showDistinctId = null;
    },

    async getAllCampaigns() {
      const { data } = await this.$http.get('/campaigns/get/all');
      this.allCampaigns = [{ name: 'No Linked ND Campaign', uuid: null }, ...data];
    },

    addIntegration() {
      if (this.showForm && !this.isEditMode) {
        this.showForm = false; this.resetForm(); this.activeRowId = null;
        return;
      }
      this.resetForm();
      this.activeRowId = null;
      this.showForm = true;
      this.isEditMode = false;
    },

    editIntegration(integration) {
      if (this.showForm && this.activeRowId === integration.uuid) {
        this.showForm = false; this.isEditMode = false; this.resetForm(); this.activeRowId = null;
        return;
      }
      this.activeRowId = integration.uuid;
      this.form = { ...integration };
      this.showForm = true;
      this.isEditMode = true;
      this.currentIntegrationId = integration.uuid;
    },

    resetForm() {
      this.form = {
        name: '',
        start_date: '',
        end_date: '',
        integration_codes_csv: '',
        disallowed_event_names_csv: '',  // added
        native_display_campaign_uuid: null,
      };
    },

    async submitForm() {
      const url    = this.isEditMode
        ? `/new-integrations/${this.currentIntegrationId}`
        : '/new-integrations';
      const method = this.isEditMode ? this.$http.put : this.$http.post;
      await this.$http_request(method, url, {
        success: `Integration ${this.isEditMode ? 'updated' : 'added'} successfully`,
        error:   `Failed to ${this.isEditMode ? 'update' : 'add'} integration`
      }, this.form);

      this.showForm = false;
      this.resetForm();
      this.fetchIntegrations();
      this.activeRowId = null;
    },

    async deleteIntegration(uuid) {
      if (!confirm("Are you sure you want to delete this integration?")) return;
      await this.$http_request(this.$http.delete, `/new-integrations/${uuid}`, {
        success: 'Integration successfully deleted.',
        error:   'Failed to delete integration.'
      });
      this.fetchIntegrations();
    },

    setCurrentCampaign(campaign) {
      this.form.native_display_campaign_uuid = campaign.uuid;
    },

    integrationCampaignName(integration) {
      if (!integration.native_display_campaign_uuid) return 'No Linked ND Campaign';
      return this.allCampaigns.find(c => c.uuid === integration.native_display_campaign_uuid)?.name
        || 'No Linked ND Campaign';
    },

    formatToMMddYYYY(dateStr) {
      return dateStr ? moment.utc(dateStr).format('MM-DD-YYYY') : '';
    },
  }
}
</script>
